import {
  CopyOutlined,
  EyeOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import { HelpButton } from '@components/help/HelpButton'
import MembershipProfile from '@components/membership/membership-profile/MembershipProfile'
import MembershipSubscription from '@components/membership/subscription/MembershipSubscription'
import Page from '@components/Page/Page'
import { TableFilters } from '@components/TableFilters'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import {
  Subscription,
  subscriptionRequestStatuses,
} from '@models/membership/Subscription'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getSubscriptions,
  getSubscriptionWithId,
  selectSubscriptions,
  setSelectedSubscription,
  setSubscriptionQuery,
} from '@store/membership/SubscriptionReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { currencyFormat, currencySymbolOf } from '@utils/currencyFormatter'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import { formatSnakeCaseLabel } from '@utils/string'
import {
  App,
  Button,
  Col,
  Flex,
  Input,
  Select,
  Space,
  TableColumnsType,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

const { Text } = Typography
const RequestsPage = () => {
  const { message } = App.useApp()
  const { isOpen } = useSBRM()

  const [modalIsVisible, setModalIsVisible] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const subscriptions = useAppSelector(selectSubscriptions())
  const { query, isLoading } = useAppSelector((state) => state.subscription)

  const columns: TableColumnsType<Subscription> = [
    {
      title: 'Members',
      fixed: 'left',
      render: (record: Subscription) => (
        <Space>
          {(record.membershipProfiles ?? []).map((person, index) => (
            <MembershipProfile.Avatar noLinkToDetails key={index} id={person} />
          ))}
        </Space>
      ),
    },
    {
      title: 'Submission date',
      key: 'submissionDate',
      sorter: true,
      render: (record: Subscription) => (
        <Tag
          color={
            dayjs().diff(dayjs(record.createdAt), 'hour') < 24
              ? 'blue'
              : dayjs().diff(dayjs(record.createdAt), 'hour') < 48
                ? 'orange'
                : 'red'
          }
        >
          {dayjs(record.createdAt).format('DD/MM/YY')}
        </Tag>
      ),
    },
    {
      title: 'Plan',
      render: (record: Subscription) => (
        <Tag color="blue" style={{ textTransform: 'capitalize' }}>
          {record.plan?.fr.name}
        </Tag>
      ),
    },

    {
      title: 'Price',
      render: (record: Subscription) => (
        <>
          {currencyFormat(
            record.plan?.price,
            true,
            true,
            false,
            currencySymbolOf(record.plan?.program?.currency)
          )}
        </>
      ),
    },
    {
      title: (
        <Tooltip
          placement="topRight"
          title={
            <Flex vertical gap={8} style={{ padding: 4 }}>
              <Text style={{ color: 'white' }}>
                For more information about subscription request statuses, please
                refer to the documentation.
              </Text>
              <HelpButton articleId="17" label="Read the documentation" />
            </Flex>
          }
        >
          <Flex justify="space-between">
            Status <InfoCircleOutlined />
          </Flex>
        </Tooltip>
      ),
      render: (record: Subscription) => (
        <Tag>{formatSnakeCaseLabel(record.status)}</Tag>
      ),
    },
    {
      title: 'Tags',
      render: (record: Subscription) => (
        <MembershipSubscription.TagManager
          id={record.id}
          onChange={() => fetchSubscriptions(params)}
        />
      ),
    },
    {
      align: 'right',
      render: (record: Subscription) => (
        <Space>
          {(record.status === 'awaiting_payment_data' ||
            record.status === 'payment_failed') && (
            <Button
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard
                  .writeText(record.frontendUrl)
                  .then(() => {
                    message.success('Payment link copied to clipboard!')
                  })
                  .catch(() => {
                    message.error('Failed to copy payment link.')
                  })
              }}
            >
              Payment link
            </Button>
          )}
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => {
              dispatch(setSelectedSubscription(record.id))
              // The table rows are trimmed to their columns; the review
              // modal reads the full subscription and its members.
              dispatch(getSubscriptionWithId(record.id))
              setModalIsVisible(true)
            }}
          >
            View
          </Button>
          {(record.status === 'awaiting_payment_data' ||
            record.status === 'to_review') && (
            <SBRM.Actions
              actions={[SBRMAction.Delete]}
              entity={Entity.membershipSubscriptions}
              entityId={record.id}
            />
          )}
        </Space>
      ),
    },
  ]

  const fetchSubscriptions = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      ...query,
      columnKey: params.sort.columnKey ?? 'submissionDate',
      order: params.sort.order ?? 'descend',
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
      filters: {
        ...params.filters,
        status:
          params.filters.status === undefined
            ? subscriptionRequestStatuses
            : params.filters.status,
      },
      search: params.search,
    }

    dispatch(setSubscriptionQuery(newQuery))
    dispatch(getSubscriptions(newQuery))
  }

  const { handleTableChange, handleFiltersChange, params } =
    useTableUrlSync<Subscription>({ onParamsChange: fetchSubscriptions })

  useEffect(() => {
    if (isOpen) return

    fetchSubscriptions(params)
  }, [isOpen])

  const selectOptionsStatus = subscriptionRequestStatuses.map((status) => ({
    value: status,
    label: formatSnakeCaseLabel(status),
  }))

  return (
    <Page
      noWrapWithCol
      permissions={Permission.manageRequestsMembership}
      pageTitle={{
        title: 'Requests',
        level: 4,
        badge: { count: query.pagination?.total, overflowCount: 9999999 },
      }}
    >
      <Col span={24}>
        <TableFilters
          initialValues={{
            plans: params.filters.plans?.map(Number),
            tags: params.filters.tags?.map(Number),
            status: params.filters.status,
            name: params.search.name,
          }}
          onValuesChange={(filters: any, search: any) => {
            if (filters.status && filters.status.length === 0) {
              delete filters.status
            }

            handleFiltersChange(filters, search)
          }}
          left={[
            {
              name: 'plans',
              type: 'filter',
              label: 'Plans',
              input: (
                <SBAsyncSelect
                  style={{ minWidth: '16ch' }}
                  multiple
                  treeSelectProps={{
                    maxTagCount: 'responsive',
                  }}
                  placeholder="Select plans"
                  entity={Entity.membershipPlan}
                  allowClear
                />
              ),
            },
            {
              name: 'tags',
              type: 'filter',
              label: 'Tags',
              input: (
                <SBAsyncSelect
                  style={{ minWidth: '16ch' }}
                  multiple
                  selectProps={{
                    maxTagCount: 'responsive',
                  }}
                  entity={Entity.subscriptionTag}
                  placeholder="Select tags"
                  allowClear
                />
              ),
            },
            {
              name: 'status',
              type: 'filter',
              label: 'Status',
              input: (
                <Select
                  style={{ minWidth: '22ch' }}
                  options={selectOptionsStatus}
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder="Select status"
                  allowClear
                />
              ),
            },
          ]}
          right={[
            {
              name: 'name',
              type: 'search',
              label: 'Search by name',
              input: <Input placeholder="Search member" allowClear />,
            },
            {
              type: 'button',
              input: <HelpButton articleId="87" />,
            },
            {
              type: 'button',
              input: (
                <SBRM.Actions
                  actions={[SBRMAction.Create]}
                  entity={Entity.membershipSubscriptions}
                />
              ),
            },
          ]}
        />
        <SBTable
          columns={columns}
          dataSource={subscriptions}
          loading={isLoading}
          onChange={handleTableChange}
          pagination={query.pagination}
          scroll={{ x: 'max-content' }}
        />
      </Col>

      <MembershipSubscription.ReviewModal
        isVisible={modalIsVisible}
        onClose={() => {
          setModalIsVisible(false)
          dispatch(setSelectedSubscription(0))
        }}
      />
    </Page>
  )
}

export default RequestsPage
